import app from '../firebase/config';
import { bankQuestion } from './campaign';

/**
 * Rob's approved first answers for the ad questions (BRAND_SPEC §14.5).
 *
 * A link that lands with a bank question (/?q=q02, /q/q02) asks it for the
 * visitor. When Rob has approved that question's first answer, the app shows
 * it at once, word for word, with its sources, and makes no model call;
 * otherwise AI Rob answers live (ChatView askLanding). Two places hold an
 * approval:
 *
 *   1. meta/canonicalAnswers, the public Firestore document the team portal
 *      writes when someone approves an answer (functions/canonicalAnswers.js),
 *      so an approval reaches visitors without a deploy, and one taken down
 *      leaves it at once. One plain read over Firestore's REST endpoint (the
 *      document is public, like meta/publicConfig), started as the page opens
 *      (prefetchApprovedAnswer) and waited for at most WAIT_MS when the
 *      question is asked. When it can be read, it decides;
 *   2. the bundle's bank (src/data/question-bank.json, canonicalAnswer status
 *      "approved"; scripts/canonical-answers.js --bake copies approvals into it
 *      before a build): only when the document can't be read (offline, refused,
 *      or slower than BUNDLED_WAIT_MS), so a take-down in the portal is never
 *      undone by the copy a build still carries. Neither: a live answer, never
 *      a stuck page.
 *
 * Nothing here is shown unless the record says approved and carries words:
 * a pending or rejected draft never reaches a visitor.
 */

const WAIT_MS = 2500;
// With the bundle's copy in hand, the read is waited for this long at most.
const BUNDLED_WAIT_MS = 1000;
const FETCH_TIMEOUT_MS = 6000;
const DOC_PATH = 'meta/canonicalAnswers';

let fetched = null; // the one read per tab: a promise of { [id]: answer } ({} when none), or null when unread

function projectId() {
  try {
    return (app && app.options && app.options.projectId) || 'askpastorrob-b6812';
  } catch (e) {
    return 'askpastorrob-b6812';
  }
}

/** One Firestore REST value as plain JSON (strings, numbers, booleans, null, maps, arrays, timestamps). */
export function decodeValue(v) {
  if (!v || typeof v !== 'object') return null;
  if ('stringValue' in v) return v.stringValue;
  if ('integerValue' in v) return Number(v.integerValue);
  if ('doubleValue' in v) return v.doubleValue;
  if ('booleanValue' in v) return v.booleanValue;
  if ('nullValue' in v) return null;
  if ('timestampValue' in v) return v.timestampValue;
  if ('mapValue' in v) return decodeFields((v.mapValue && v.mapValue.fields) || {});
  if ('arrayValue' in v) return ((v.arrayValue && v.arrayValue.values) || []).map(decodeValue);
  return null;
}

export function decodeFields(fields) {
  const out = {};
  Object.keys(fields || {}).forEach((k) => { out[k] = decodeValue(fields[k]); });
  return out;
}

// "Rob", "Pastor Rob" or "Rob Hall": the names the founding team's owner entry goes by
// (functions/roles.js ROB_LABEL), for an approval kept before approvedByRob existed.
const ROB_NAME = /^\s*(?:pastor\s+)?rob(?:\s+hall)?\s*$/i;

/**
 * An approved answer's record as the app uses it, or null for anything that is
 * not one. byRob: whether Pastor Rob approved it himself (approvedByRob; else,
 * for an older record, whether approvedBy is his name). Only then does a
 * visitor read "Rob approved this answer"; anyone else's approval reads
 * "approved by The Net's team" (ChatView).
 */
export function approvedRecord(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const text = typeof entry.text === 'string' ? entry.text.trim() : '';
  if (!text) return null;
  if (entry.status && entry.status !== 'approved') return null;
  const approvedBy = typeof entry.approvedBy === 'string' ? entry.approvedBy : null;
  return {
    text,
    sources: Array.isArray(entry.sources) ? entry.sources.filter((s) => s && typeof s === 'object') : [],
    ...(entry.ground && typeof entry.ground === 'object' ? { ground: entry.ground } : {}),
    approvedBy,
    approvedOn: typeof entry.approvedOn === 'string' ? entry.approvedOn : null,
    byRob: typeof entry.approvedByRob === 'boolean' ? entry.approvedByRob : ROB_NAME.test(approvedBy || ''),
  };
}

/** The bundle's approval for a bank question, or null. */
export function bundledAnswer(question) {
  const q = question && question.id ? question : bankQuestion(question);
  return q ? approvedRecord(q.canonicalAnswer) : null;
}

async function readPublished() {
  const url = `https://firestore.googleapis.com/v1/projects/${encodeURIComponent(projectId())}/databases/(default)/documents/${DOC_PATH}`;
  const signal = typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(FETCH_TIMEOUT_MS) : undefined;
  const res = await fetch(url, { signal, cache: 'no-store' });
  if (res.status === 404) return {};
  if (!res.ok) throw new Error(`meta/canonicalAnswers: HTTP ${res.status}`);
  const body = await res.json();
  const doc = decodeFields((body && body.fields) || {});
  const answers = doc.answers && typeof doc.answers === 'object' ? doc.answers : {};
  const out = {};
  Object.keys(answers).forEach((id) => {
    const record = approvedRecord(answers[id]);
    if (record) out[id] = record;
  });
  return out;
}

/**
 * Starts the one read of the published approvals for this tab, for a question
 * a landing asks (a statement or "your turn" asks nothing, so has no first
 * answer). Even when the bundle carries its approval: the public document is
 * what says the approval still stands. Safe to call any number of times.
 */
export function prefetchApprovedAnswer(id) {
  const q = bankQuestion(id);
  if (!q || q.mode !== 'asking') return;
  if (!fetched) fetched = readPublished().catch(() => null);
}

/**
 * The approved first answer for a bank question: the published one when the
 * public document could be read (waiting at most `waitMs` for it, or
 * BUNDLED_WAIT_MS when the bundle has a copy), and nothing when it says none,
 * so an approval taken down in the portal stays down; else, unread, the
 * bundle's; else null. Never throws: the caller asks AI Rob live on null.
 */
export async function approvedAnswerFor(question, { waitMs = WAIT_MS } = {}) {
  const q = question && question.id ? question : bankQuestion(question);
  if (!q) return null;
  const bundled = approvedRecord(q.canonicalAnswer);
  try {
    prefetchApprovedAnswer(q.id);
    const wait = bundled ? Math.min(waitMs, BUNDLED_WAIT_MS) : waitMs;
    const published = fetched ? await Promise.race([
      fetched,
      new Promise((resolve) => setTimeout(() => resolve(null), wait)),
    ]) : null;
    if (published) return published[q.id] || null;
    return bundled;
  } catch (e) {
    return bundled;
  }
}

/** For tests: forget the read, so the next call fetches again. */
export function resetApprovedAnswers() {
  fetched = null;
}
